import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import { directionFromVector } from "../direction";
import { ParticleEffect } from "../effects/particleEffect";
import { ToxicSlugAI } from "../ai/toxicSlugAI";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { randomChance } from "../random";
import { deserializeXY } from "../serialization";
import { BilePuddle } from "../things/bilePuddle";
import { GoldDrop } from "../things/goldDrop";
import { LevelChangerThing } from "../things/levelChangerThing";
import { ToxicSlugBrokenShell } from "../things/toxicSlugBrokenShell";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { BileGas } from "../things/bileGas";
import type { Thing } from "../thing";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { XY } from "../xy";

const PUDDLE_TRAIL_CHANCE = 0.7;
const BUBBLE_HIT_GAS_AMOUNT = 6;

export class ToxicSlug extends Mob {
	static key = "ToxicSlug";
	private ai: ToxicSlugAI;

	constructor(xy: XY) {
		super(xy);
		this.setMovementPeriod(2);
		this.setAttackPeriod(2);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0.25);
		this.damageMultipliers.set(DamageType.MELEE, 0.5);
		this.ai = new ToxicSlugAI(this);
	}

	protected override onZeroHp(source: Thing | null, type: DamageType | null): void {
		const bounty = this.gold;
		this.gold = 0;
		super.onZeroHp(source, type);
		if (!this.board) return;
		const shell = this.placeShell();
		if (bounty <= 0) return;
		if (shell !== null) {
			shell.gold = bounty;
		} else if (this.board.isPassable(this.xy)) {
			GoldDrop.spawnOrMerge(this.board, this.xy, bounty);
		}
	}

	private placeShell(): ToxicSlugBrokenShell | null {
		if (this.isOverOpenChasmOrLava()) return null;
		const onLevelExit = this.board!.getThingsAt(this.xy).some((t) => t instanceof LevelChangerThing);
		if (onLevelExit) return null; // No shell on level exits — a blocking obstacle there could trap the player.
		const shell = new ToxicSlugBrokenShell(this.xy);
		this.board!.addThing(shell);
		return shell;
	}

	// Solidifier hits stall the slither and vent bile gas instead of bubbling.
	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		if (type === DamageType.BUBBLE && this.active && this.board) {
			this.movementCadence.consume();
			BileGas.addOrPressurize(this.board, this.xy, BUBBLE_HIT_GAS_AMOUNT);
			return 0;
		}
		return super.applyDamage(type, amount, source);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLOOD;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(ToxicSlug.key);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.TOXIC_SLUG_DIE);
	}

	// Spit attack: no lunge or Hit effect; damage plus a puddle on the target tile.
	override attack(target: PhysicalThingWithHp): void {
		if (this.isFrozen()) return;
		const vector = new XY(Math.sign(target.xy.x - this.xy.x), Math.sign(target.xy.y - this.xy.y));
		const direction = directionFromVector(vector) ?? undefined;
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.MUZZLE_FLASH_BILE, 8, direction));
		this.board!.addNonPhysicalThing(new Sound(SoundId.TOXIC_SLUG_ATTACK, this.xy));
		target.applyDamage(DamageType.MELEE, this.getAttackDamage(), this);
		BilePuddle.placeOrRefresh(this.board!, target.xy);
		this.attackCadence.consume();
	}

	override moveTo(xy: XY): void {
		const previousXY = this.xy;
		super.moveTo(xy);
		if (randomChance(PUDDLE_TRAIL_CHANCE)) {
			BilePuddle.placeOrRefresh(this.board!, previousXY);
		}
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	static deserialize(data: any): ToxicSlug {
		const m = new ToxicSlug(deserializeXY(data.xy));
		m.restoreMobFields(data);
		return m;
	}
}
